import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { supabase } from "../../lib/supabase";
import {
  Home,
  Building2,
  ScrollText,
  FileJson,
  Settings,
  Layers,
  Key,
  Bell,
} from "lucide-react";
import { SaasDetails } from "./saas/SaasDetails";
import { ConnectedHotels } from "./saas/ConnectedHotels";
import { SaasPMSLogs } from "./saas/SaasPMSLogs";
import { ApiDocumentation } from "./saas/ApiDocumentation";
import { SaasSettings } from "./saas/SaasSettings";
import { FunctionalScope } from "./saas/FunctionalScope";
import { ApiToken } from "./saas/ApiToken";
import { UserMenu } from "../UserMenu";
import { ThemeToggle } from "../ThemeToggle";
import { ThemeProvider } from "../../contexts/ThemeContext";
import { NotificationCenter } from "../shared/NotificationCenter";
import { NotificationBell } from "../shared/NotificationBell";

export function SaasExtranet() {
  const location = useLocation();
  const [activeSection, setActiveSection] = useState("details");
  const [userEmail, setUserEmail] = useState("");

  useEffect(() => {
    const getUser = async () => {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (user) {
        setUserEmail(user.email || "");
      }
    };
    getUser();

    // Check if we should show settings with user tab
    if (location.state?.settingsTab === "user") {
      setActiveSection("settings");
    }
  }, [location.state]);

  const renderContent = () => {
    switch (activeSection) {
      case "details":
        return <SaasDetails />;
      case "hotels":
        return <ConnectedHotels />;
      case "logs":
        return <SaasPMSLogs />;
      case "api":
        return <ApiDocumentation />;
      case "apiToken":
        return <ApiToken />;
      case "settings":
        return <SaasSettings />;
      case "scope":
        return <FunctionalScope />;
      case "notifications":
        return <NotificationCenter />;
      default:
        return <SaasDetails />;
    }
  };

  const menuItems = [
    { id: "details", label: "SaaS Details", icon: Home },
    { id: "hotels", label: "Connected Hotels", icon: Building2 },
    { id: "scope", label: "Functional Scope", icon: Layers },
    { id: "logs", label: "PMS Logs", icon: ScrollText },
    { id: "api", label: "API Documentation", icon: FileJson },
    { id: "apiToken", label: "API Token", icon: Key },
    { id: "notifications", label: "Notifications", icon: Bell },
    { id: "settings", label: "Settings", icon: Settings },
  ];

  return (
    <ThemeProvider>
      <div className="extranet-layout">
        <div className="extranet-sidebar">
          {/* Logo */}
          <div className="p-4">
            <div className="flex flex-col">
              <div className="w-32">
                <img
                  src="/images/omnylink_logo.png"
                  alt="Omnylink"
                  className="w-full h-auto block"
                />
                <img
                  src="/images/omnylink_logo_white.png"
                  alt="Omnylink"
                  className="w-full h-auto hidden"
                />
              </div>
              <span className="mt-1 text-sm font-medium text-gray-600">
                for SaaS
              </span>
            </div>
          </div>

          {/* Navigation Menu */}
          <nav className="flex-1 p-4 space-y-1">
            {menuItems.map((item) => {
              const Icon = item.icon;
              return (
                <button
                  key={item.id}
                  onClick={() => setActiveSection(item.id)}
                  className={`
                    w-full flex items-center px-3 py-2 text-sm font-medium rounded-md
                    ${
                      activeSection === item.id
                        ? "bg-indigo-50"
                        : "text-gray-600"
                    }
                  `}
                >
                  <Icon
                    className={`
                    h-5 w-5 mr-3
                    ${
                      activeSection === item.id
                        ? "text-indigo-500"
                        : "text-gray-400"
                    }
                  `}
                  />
                  {item.label}
                </button>
              );
            })}
          </nav>

          {/* User Menu and Theme Toggle */}
          <div className="p-4 flex items-center justify-between">
            <UserMenu email={userEmail} />
            <ThemeToggle />
          </div>
        </div>

        {/* Main Content */}
        <div className="extranet-content">
          {/* Header with notification bell */}
          <div className="bg-white border-b border-gray-200 px-4 py-2 flex justify-end items-center">
            <NotificationBell />
          </div>
          <div className="extranet-content-inner">{renderContent()}</div>
        </div>
      </div>
    </ThemeProvider>
  );
}
